import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BUSINESS_STRUCTURES, COMMON_MISTAKES } from '../data/mock';
import { CalendlyEmbed } from '../components/CalendlyEmbed';
import { Badge } from '../components/ui/badge';
import * as Icons from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.5, ease: 'easeOut' },
};

export default function BusinessStructurePage() {
  return (
    <main>
      {/* Hero */}
      <section className="pt-32 pb-16 px-6" style={{ background: 'radial-gradient(at 53% 78%, hsla(60,100%,50%,0.15) 0px, transparent 50%), radial-gradient(at 71% 91%, hsla(108,100%,50%,0.15) 0px, transparent 50%)' }}>
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div {...fadeInUp}>
            <Badge className="mb-5 bg-[rgba(148,242,127,0.12)] text-[rgb(13,121,22)] border-[rgba(148,242,127,0.3)] px-4 py-1.5 text-xs font-medium rounded-full">
              Business Education
            </Badge>
          </motion.div>
          <motion.h1 {...fadeInUp} transition={{ delay: 0.1 }}
            className="font-bold text-[rgb(0,55,32)] tracking-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 3.5rem)', lineHeight: '0.92' }}>
            Choose the Right Business Structure
          </motion.h1>
          <motion.p {...fadeInUp} transition={{ delay: 0.2 }}
            className="text-[rgb(131,146,140)] max-w-[600px] mx-auto" style={{ fontSize: 'clamp(1rem, 2vw, 1.15rem)' }}>
            Understanding entity types from a tax perspective is critical to building a strong financial foundation.
          </motion.p>
          <motion.div {...fadeInUp} transition={{ delay: 0.3 }} className="flex items-center justify-center gap-2 mt-4">
            <span className="text-lg font-bold text-[rgb(0,55,32)]">Starts at $599</span>
            <Link to="/book" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-br from-[#8FEC78] to-[#81DD67] text-white font-semibold text-sm no-underline hover:shadow-md transition-shadow">
              Book Free Consult
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Entity Comparison */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.5rem)' }}>Entity Types Explained</h2>
            <p className="text-[rgb(131,146,140)] mt-2">A high-level overview to help you understand your options.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {BUSINESS_STRUCTURES.map((entity, i) => (
              <motion.div key={entity.type} {...fadeInUp} transition={{ delay: i * 0.08 }}
                className="border border-black/10 rounded-xl p-6 hover:shadow-lg transition-all duration-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[rgba(148,242,127,0.12)] flex items-center justify-center">
                    <Icons.Landmark className="w-4 h-4 text-[rgb(13,121,22)]" />
                  </div>
                  <h3 className="font-semibold text-[rgb(0,55,32)] text-base">{entity.type}</h3>
                </div>
                <p className="text-sm text-[rgb(131,146,140)] leading-relaxed mb-3">{entity.description}</p>
                <div className="mb-3">
                  <h4 className="text-xs font-semibold text-[rgb(0,55,32)] uppercase tracking-wider mb-1.5">Tax Implications</h4>
                  <p className="text-sm text-[rgb(14,15,12)] leading-relaxed">{entity.taxImplications}</p>
                </div>
                <div className="mb-3">
                  <h4 className="text-xs font-semibold text-[rgb(0,55,32)] uppercase tracking-wider mb-1.5">Best For</h4>
                  <p className="text-sm text-[rgb(131,146,140)] leading-relaxed">{entity.bestFor}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-[rgb(0,55,32)] uppercase tracking-wider mb-1.5">Key Considerations</h4>
                  <ul className="space-y-1.5">
                    {entity.considerations.map((c, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-[rgb(131,146,140)]">
                        <Icons.Check className="w-3.5 h-3.5 text-[#8FEC78] mt-0.5 shrink-0" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16" style={{ background: 'rgba(0,0,0,0.02)' }}>
        <div className="max-w-[800px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-10">
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight mb-2" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>Common Mistakes to Avoid</h2>
            <p className="text-sm text-[rgb(131,146,140)]">We see these issues regularly. Don't let them cost you.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {COMMON_MISTAKES.map((mistake, i) => (
              <motion.div key={i} {...fadeInUp} transition={{ delay: i * 0.06 }}
                className="flex items-start gap-3 bg-white border border-black/10 rounded-xl p-4">
                <Icons.AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-sm text-[rgb(14,15,12)]">{mistake}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Need */}
      <section className="py-16 bg-white">
        <div className="max-w-[800px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-10">
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>What We Need From You</h2>
          </motion.div>
          <motion.div {...fadeInUp} className="bg-[rgba(0,0,0,0.02)] border border-black/10 rounded-xl p-6">
            <ul className="space-y-3">
              {['A description of your business or business idea', 'Projected income estimates', 'Number of owners or members', 'Your preferred state of formation', 'Any existing business documents (if restructuring)'].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[rgb(14,15,12)]">
                  <Icons.FileCheck className="w-4 h-4 text-[rgb(13,121,22)] mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-white">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <Icons.AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800 mb-1">Important Disclaimer</p>
                <p className="text-xs text-amber-700 leading-relaxed">This information is provided for tax education purposes only and does not constitute legal advice. We provide tax education and filing support to help you make informed decisions. Always consult with a qualified legal professional for legal matters related to business formation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA + Calendly */}
      <section className="py-20" style={{ background: 'rgba(0,0,0,0.02)' }}>
        <div className="max-w-[900px] mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-10">
            <h2 className="font-semibold text-[rgb(0,55,32)] tracking-tight mb-3" style={{ fontSize: 'clamp(1.875rem, 4vw, 2.5rem)' }}>Ready to Structure Your Business?</h2>
            <p className="text-[rgb(131,146,140)]">Book a free consultation and let's find the right entity for your situation.</p>
          </motion.div>
          <motion.div {...fadeInUp} className="rounded-2xl border border-black/10 overflow-hidden bg-white shadow-sm">
            <CalendlyEmbed minimal />
          </motion.div>
        </div>
      </section>
    </main>
  );
}
